import type { LoaderFunction } from 'react-router-dom';

import { lazy } from 'react';
import React from 'react';

import AllRoutesError from './all-routes-wrapper/all-routes-error';
import { CIRCLE_ROUTES } from './routes';

const NotFoundPage = lazy(() => import('pages/404/404'));
const RootPage = lazy(() => import('pages/root/root'));

const AllRoutesWrapper = lazy(() => import('./all-routes-wrapper/all-routes-wrapper'));
const AuthRoutesWrapper = lazy(() => import('./auth-routes-wrapper'));
const PrivateRoutesWrapper = lazy(
  () => import('./private-routes-wrapper/private-routes-wrapper'),
);

const ExamHelp = lazy(() => import('../screens/exam-help'));

const LoginWrapper = lazy(() => import('../pages/login'));

const ResetPassword = lazy(() => import('../screens/reset-password'));

const Forgotpassword = lazy(() => import('../screens/forgot-password'));
const DashboardHome = lazy(() => import('../screens/dashboard/home'));
const Elective = lazy(() => import('../screens/elective'));

const SheetReview = lazy(() => import('../screens/sheet-review'));
const WorksheetContainer = lazy(() => import('../screens/curriculum-worksheet'));
const PendingSheets = lazy(() => import('../screens/pending'));
const DemoReport = lazy(() => import('../screens/demo-report'));
const Profile = lazy(() => import('../screens/profile'));
const Account = lazy(() => import('../pages/account'));
const ExamPerformanceForm = lazy(() => import('../screens/exam-performance-form'));

const WinsDashboard = lazy(() => import('../screens/wins-dashboard'));
const ClassRecordings = lazy(() => import('../screens/class-recordings'));
const WatchClassRecordings = lazy(() => import('../screens/watch-class-recording'));

const ChapterAndSheetContainer = lazy(() => import('../screens/chapter-sheet-container'));

const StudentChapter = lazy(() => import('../pages/home/chapter/chapter'));

const ReminderResponseScreen = lazy(() => import('../screens/reminder-response'));

const MonthlyProgressReportV2 = lazy(
  () => import('../screens/monthly-progress-report-v2'),
);

const MasteryDashboardRoute = lazy(() => import('../modules/mastery-dashboard'));

const ParentFeedback = lazy(() => import('../screens/parent-feedback'));

/************************************************* Circle Pages Start *************************************************/
const CircleHomePage = lazy(() => import('pages/circle/home/home'));
const CircleGame = lazy(() => import('../pages/circle/game/game'));
const CircleLeaderboard = lazy(() => import('../pages/circle/leaderboard/leaderboard'));
/************************************************** Circle Pages End **************************************************/

const HomePage = lazy(() => import('pages/home/home'));
const WorksheetResumePage = lazy(() => import('pages/worksheet/resume/worksheet-resume'));
const WorksheetReviewPage = lazy(() => import('pages/worksheet/review/worksheet-review'));
const WorksheetPreviewPage = lazy(
  () => import('pages/worksheet/preview/worksheet-preview'),
);
const SATMockTestPage = lazy(() => import('pages/worksheet/sat-mock-test/sat-mock-test'));

export interface IRouteObject {
  name: string;
  path?: string;
  element?: JSX.Element;
  errorElement?: JSX.Element;
  loader?: LoaderFunction;
  children?: IRouteObject[];
  isPublicAndPrivate?: boolean;
}

const ALL_ROUTES: IRouteObject[] = [];

const PRIVATE_ROUTES: IRouteObject = {
  name: 'Private Routes Wrapper',
  element: <PrivateRoutesWrapper allRoutes={ALL_ROUTES} />,
  children: [
    {
      path: '',
      element: <RootPage />,
      name: 'Root',
    },
    { path: '/home', element: <HomePage />, name: 'Home' },
    { path: '/legacy-home', element: <DashboardHome />, name: 'Home Legacy' },
    {
      path: '/chapter/:chapterId',
      element: <ChapterAndSheetContainer />,
      name: 'Chapter',
    },
    {
      path: '/chapters/:userChapterId',
      element: <StudentChapter />,
      name: 'Chapters',
    },
    { path: '/profile', element: <Profile />, name: 'Profile' },
    { path: '/account', element: <Account />, name: 'Profile' },
    {
      path: '/pending',
      element: <PendingSheets />,
      name: 'Pending Sheets',
    },
    {
      name: 'Worksheet',
      path: '/worksheet/',
      element: <WorksheetContainer />,
    },
    {
      path: '/sheets/:sheetId/',
      element: <ChapterAndSheetContainer />,
      name: 'Sheet',
    },
    {
      path: '/sheets/:sheetId/review/',
      element: <SheetReview />,
      name: 'Sheet Review',
    },
    {
      path: '/worksheets/:nodeId/resume',
      element: <WorksheetResumePage />,
      name: 'Worksheet Resume',
    },
    {
      path: '/worksheets/:studentNodeId/review',
      element: <WorksheetReviewPage />,
      name: 'Worksheet Review',
    },
    {
      path: '/worksheets/preview',
      element: <WorksheetPreviewPage />,
      name: 'Worksheet Preview',
    },
    {
      path: '/sat-mock-test/:blockId/resume',
      element: <SATMockTestPage />,
      name: 'SAT Mock Test',
    },
    { path: '/elective', element: <Elective />, name: 'Elective' },
    { path: '/tests', element: <ExamHelp />, name: 'Exam Help' },
    { path: '/wins', element: <WinsDashboard />, name: 'Wins' },
    { path: '/class-recordings', element: <ClassRecordings />, name: 'Class Recordings' },
    {
      path: '/class-recordings/watch',
      element: <WatchClassRecordings />,
      name: 'Watch Class Recording',
    },
    {
      path: '/smart-practice-dashboard',
      element: <MasteryDashboardRoute />,
      name: 'Smart Practice',
    },
    {
      path: '/demo-report/:studentClassroomId',
      element: <DemoReport />,
      name: 'Demo Report',
      isPublicAndPrivate: true,
    },
    {
      path: CIRCLE_ROUTES.HOME.PATH,
      element: <CircleHomePage />,
      name: CIRCLE_ROUTES.HOME.NAME,
    },
    {
      path: CIRCLE_ROUTES.GAME.PATH,
      element: <CircleGame />,
      name: CIRCLE_ROUTES.GAME.NAME,
    },
    {
      path: CIRCLE_ROUTES.LEADERBOARD.PATH,
      element: <CircleLeaderboard />,
      name: CIRCLE_ROUTES.LEADERBOARD.NAME,
    },
  ],
};

const AUTH_ROUTES: IRouteObject = {
  name: 'Auth Routes Wrapper',
  element: <AuthRoutesWrapper />,
  children: [
    {
      path: '/login/',
      element: <LoginWrapper />,
      name: 'Login',
    },
    {
      path: '/forgotpassword',
      element: <Forgotpassword />,
      name: 'Forgot Password',
    },
    {
      path: '/reset-password/:token',
      element: <ResetPassword />,
      name: 'Reset Password',
    },
  ],
};

const PUBLIC_ROUTES: IRouteObject = {
  name: 'Public Routes',
  children: [
    {
      path: '/reminder-response/:classroomId/',
      element: <ReminderResponseScreen />,
      name: 'Reminder Response',
    },
    { path: '/mpr/:studentId/', element: <MonthlyProgressReportV2 />, name: 'Mpr' },
    {
      path: '/exam-performance/:examPerformanceId/',
      element: <ExamPerformanceForm />,
      name: 'Exam Performance',
    },
    {
      path: '/progress-report/:studentId/',
      element: <MonthlyProgressReportV2 />,
      name: 'Timeline',
    },
    {
      path: '/parent-feedback/:classroomId/',
      element: <ParentFeedback />,
      name: 'Parent Feedback',
    },
  ],
};

const NO_MATCH_ROUTE: IRouteObject = {
  name: '404 - Page Not Found',
  path: '*',
  element: <NotFoundPage />,
};

ALL_ROUTES.push({
  name: 'All Routes Wrapper',
  element: <AllRoutesWrapper />,
  errorElement: <AllRoutesError />,
  children: [PRIVATE_ROUTES, AUTH_ROUTES, PUBLIC_ROUTES, NO_MATCH_ROUTE],
});

export { ALL_ROUTES };
